// Phase 1A Step 26 / 9C.10a — The Brief per-mode shell.
//
// Sits inside <UnifiedShell mode="brief">, providing the dossier
// framing for Brief surfaces (Phantom slip review, Phantom slip
// history, and the future real-slip presentation flow). Per
// UI-architecture §5: sidebar-free, centered single-column, max-
// width 640px.
//
// 9C.10a polish:
//   - Article wrapper now spreads `cardLow` + `transitionStandard`
//     via the new sibling rails file so the dossier sits on the
//     same visual plane as the 9C /home, /vault, and /exchange
//     premium surfaces.
//   - Header eyebrow gains a registered `mode.daily_intelligence`
//     icon next to the label.
//   - Sub-nav links each gain a registered icon
//     (`process.sample_valid` for Current slip, `lifecycle.settled`
//     for History) and inherit `transitionStandard` for hover.
//   - The legacy back-link to the migration-window dashboard is
//     removed. The /dashboard route remains fully functional for
//     direct URL access; the shell no longer surfaces it because
//     the migration window is closed (per Step 28 / 39 doctrine in
//     the parent styles file). `Open STRIKZA Home` link preserved.

import type { CSSProperties, ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";

import { Icon } from "../../components/icons/Icon.tsx";
import * as r from "./brief-shell-rails.styles.ts";
import * as s from "./brief-shell.styles.ts";

interface BriefShellProps {
  eyebrow?: string;
  title: string;
  description?: string;
  children: ReactNode;
}

const articleStyle: CSSProperties = { ...s.article, ...r.articleRails };

export function BriefShell({
  eyebrow,
  title,
  description,
  children,
}: BriefShellProps): JSX.Element {
  return (
    <article style={articleStyle} aria-label={title}>
      <header style={s.header}>
        {eyebrow !== undefined && eyebrow.length > 0 ? (
          <span style={r.headerLead}>
            <Icon name="mode.daily_intelligence" size="sm" decorative />
            <span style={s.eyebrow}>{eyebrow}</span>
          </span>
        ) : null}
        <h1 style={s.title}>{title}</h1>
        {description !== undefined && description.length > 0 ? (
          <p style={s.description}>{description}</p>
        ) : null}
        <nav style={s.nav} aria-label="Brief sub-navigation">
          <NavLink
            to="/brief"
            end
            style={({ isActive }) => ({
              ...r.navItemLead,
              ...(isActive ? s.navItemActive : s.navItem),
            })}
          >
            <Icon name="process.sample_valid" size="sm" decorative />
            <span>Current slip</span>
          </NavLink>
          <NavLink
            to="/brief/history"
            style={({ isActive }) => ({
              ...r.navItemLead,
              ...(isActive ? s.navItemActive : s.navItem),
            })}
          >
            <Icon name="lifecycle.settled" size="sm" decorative />
            <span>History</span>
          </NavLink>
        </nav>
        <nav style={s.backLinkRow} aria-label="Brief back-links">
          <Link to="/home" style={s.dashboardLink}>
            Open STRIKZA Home
          </Link>
        </nav>
      </header>
      <div style={s.body}>{children}</div>
    </article>
  );
}
